import { useRef } from 'react';
import { motion, useMotionValue, useSpring, useTransform } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  ShoppingCart,
  Code2,
  Server,
  Mail,
  ShieldCheck,
  Plug,
  Megaphone,
  ArrowUpRight,
} from 'lucide-react';
import { Amp } from './Amp';

const pillars = [
  {
    id: '01',
    icon: ShoppingCart,
    title: 'Shopify & eCommerce',
    tagline: 'Commerce, end-to-end',
    href: '/services/shopify',
    summary: 'Custom themes, Shopify Plus checkout, app development, migrations, and full eCommerce operations.',
    services: ['Custom Theme & UI/UX Design', 'Shopify App Development', 'Plus Checkout Extensions', 'Platform Migrations'],
    glow: 'rgba(37,99,235,0.15)',
  },
  {
    id: '02',
    icon: Code2,
    title: 'Software & Mobile',
    tagline: 'Custom builds, native to web',
    href: '/services/software',
    summary: 'Web apps, iOS / Android products, SaaS platforms, and backend systems engineered for scale.',
    services: ['Custom Web Applications', 'iOS & Android Mobile Apps', 'Backend APIs & Microservices', 'SaaS Product Engineering'],
    glow: 'rgba(37,99,235,0.18)',
  },
  {
    id: '03',
    icon: Server,
    title: 'IT Infrastructure',
    tagline: 'Networks, telephony & on-prem',
    href: '/services/it-solutions',
    summary: 'Office networks, structured cabling, VoIP, CCTV, biometrics, and the hardware backbone.',
    services: ['Office Network & Wi-Fi', 'VoIP, IVR & Call Routing', 'CCTV & Smart Surveillance', 'Servers — On-prem & Cloud'],
    glow: 'rgba(147,197,253,0.20)',
  },
  {
    id: '04',
    icon: Mail,
    title: 'Email & Workplace',
    tagline: 'Microsoft 365 · Google Workspace',
    href: '/services/email-workplace',
    summary: 'Business email, collaboration, and DNS — set up, secured, and migrated without losing a message.',
    services: ['Microsoft 365 / Google Workspace', 'Domain-based Business Email', 'Email Migration', 'Anti-Phishing & Spam Protection'],
    glow: 'rgba(59,130,246,0.18)',
  },
  {
    id: '05',
    icon: ShieldCheck,
    title: 'Cybersecurity',
    tagline: 'Secure by design',
    href: '/services/networking',
    summary: 'Defend every layer — perimeter, endpoints, identities, and inboxes — with proactive monitoring.',
    services: ['Network Security & Firewalls', 'Endpoint & Device Protection', 'Vulnerability Assessment', 'Identity & Access Management'],
    glow: 'rgba(59,130,246,0.20)',
  },
  {
    id: '06',
    icon: Plug,
    title: 'Integrations & Partnerships',
    tagline: 'Zoho · Microsoft · Interakt',
    href: '/services/integrations',
    summary: 'Connect Zoho, Microsoft, payments, logistics, and WhatsApp with reliable middleware.',
    services: ['Zoho Implementation', 'WhatsApp Marketing (Interakt)', 'Payment Gateway Integration', 'Custom API & Middleware'],
    glow: 'rgba(37,99,235,0.16)',
  },
  {
    id: '07',
    icon: Megaphone,
    title: 'Marketing & Creative',
    tagline: 'Branding · Social · Ads · SEO',
    href: '/services/marketing',
    summary: 'A full-service marketing agency — graphic design, social media, paid ads, content, and SEO to grow your brand.',
    services: ['Graphic Design & Branding', 'Social Media Management', 'Performance Marketing (Ads)', 'Content, Video & SEO'],
    glow: 'rgba(219,39,119,0.16)',
  },
];

/* Mouse-reactive Pillar Card with 3D tilt + spotlight */
const PillarCard = ({ pillar, index }) => {
  const Icon = pillar.icon;
  const cardRef = useRef(null);

  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);
  const rotX = useSpring(useTransform(my, [0, 1], [4, -4]), { stiffness: 200, damping: 22 });
  const rotY = useSpring(useTransform(mx, [0, 1], [-4, 4]), { stiffness: 200, damping: 22 });
  const spotX = useTransform(mx, (v) => `${v * 100}%`);
  const spotY = useTransform(my, (v) => `${v * 100}%`);

  const onMove = (e) => {
    const r = cardRef.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width);
    my.set((e.clientY - r.top) / r.height);
  };
  const onLeave = () => { mx.set(0.5); my.set(0.5); };

  return (
    <motion.div
      ref={cardRef}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{ duration: 0.55, delay: index * 0.06, ease: [0.22, 1, 0.36, 1] }}
      style={{ rotateX: rotX, rotateY: rotY, transformPerspective: 1500, transformStyle: 'preserve-3d' }}
      className="group relative"
    >
      {/* Soft glow behind */}
      <div
        className="absolute -inset-2 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-xl pointer-events-none"
        style={{ background: pillar.glow }}
      />

      <Link
        to={pillar.href}
        className="relative block bg-white rounded-2xl border border-[#E3E8EE] hover:border-[#2563EB]/30 transition-all duration-500 overflow-hidden h-full"
        style={{ transform: 'translateZ(20px)' }}
      >
        {/* Mouse-following spotlight */}
        <motion.div
          className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
          style={{
            background: useTransform(
              [spotX, spotY],
              ([x, y]) => `radial-gradient(400px circle at ${x} ${y}, ${pillar.glow}, transparent 60%)`
            ),
          }}
        />

        <div className="relative p-8 lg:p-9">
          {/* Top row */}
          <div className="flex items-start justify-between mb-7">
            <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-gradient-to-br from-[#EFF6FF] to-[#EFF6FF] border border-[#2563EB]/15 group-hover:scale-110 group-hover:rotate-3 transition-transform duration-500">
              <Icon size={20} className="text-[#2563EB]" />
            </div>
            <span className="font-display font-bold text-3xl leading-none text-[#E3E8EE] group-hover:text-[#2563EB] transition-colors duration-500">
              {pillar.id}
            </span>
          </div>

          {/* Tagline */}
          <span className="font-mono text-[0.55rem] tracking-[0.22em] uppercase mb-3 block text-[#2563EB] font-medium">
            {pillar.tagline}
          </span>

          {/* Title */}
          <h3 className="font-display text-[1.4rem] font-bold text-[#0F172A] mb-3 leading-tight">
            <Amp>{pillar.title}</Amp>
          </h3>

          {/* Description */}
          <p className="text-[#425466] text-[0.9rem] leading-relaxed mb-7">
            {pillar.summary}
          </p>

          {/* Services list */}
          <ul className="space-y-2 mb-7">
            {pillar.services.map((s) => (
              <li key={s} className="flex items-start gap-3 text-[0.85rem] text-[#425466]">
                <span className="w-3 h-px mt-2.5 shrink-0 bg-[#2563EB] transition-all duration-300 group-hover:w-5" />
                <span>{s}</span>
              </li>
            ))}
          </ul>

          {/* Footer */}
          <div className="flex items-center justify-between pt-5 border-t border-[#E3E8EE] group-hover:border-[#2563EB]/20 transition-colors duration-500">
            <span className="inline-flex items-center gap-2 text-[0.85rem] font-semibold text-[#2563EB] group-hover:gap-3 transition-all duration-300">
              Explore Practice
              <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </span>
          </div>
        </div>
      </Link>
    </motion.div>
  );
};

export const ServiceCategoriesSection = () => {
  return (
    <section id="services" className="section-padding bg-white relative overflow-hidden">
      <div className="container-custom relative">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-14"
        >
          <div className="flex items-center gap-3 mb-6">
            <span className="section-label">Our Expertise</span>
            <span className="w-6 h-px bg-[#2563EB]/30" />
            <span className="font-mono text-[0.55rem] tracking-widest text-[#8898AA] uppercase">
              07 Practice Areas
            </span>
          </div>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <h2
              className="font-display font-bold text-[#0F172A]"
              style={{ fontSize: 'clamp(2rem, 4.4vw, 3.75rem)' }}
            >
              Seven Specialized Practices,<br />
              <span className="italic text-gold">One Trusted Partner</span>
            </h2>
            <p className="text-[#425466] max-w-sm leading-relaxed text-sm lg:text-right">
              From storefronts to servers — we design, build, and operate the digital infrastructure modern businesses run on.
            </p>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {pillars.map((p, i) => (
            <PillarCard key={p.id} pillar={p} index={i} />
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mt-14 stripe-wash-cta rounded-2xl border border-[#E3E8EE] flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6 p-8 lg:p-10 relative overflow-hidden"
        >
          <div className="relative">
            <div className="flex items-center gap-3 mb-3">
              <span className="font-mono text-[0.6rem] tracking-[0.22em] uppercase text-[#2563EB] font-semibold">
                Need something custom?
              </span>
              <span className="w-6 h-px bg-[#2563EB]/30" />
            </div>
            <h3 className="font-display text-2xl lg:text-3xl font-bold text-[#0F172A] leading-tight max-w-xl">
              Don't see exactly what you need?{' '}
              <span className="italic text-gold">Let's talk.</span>
            </h3>
          </div>

          <Link
            to="/book"
            className="group relative inline-flex items-center gap-2.5 px-7 py-3.5 rounded-full bg-[#2563EB] text-white text-sm font-semibold transition-all duration-300 shadow-[0_4px_14px_rgba(37,99,235,0.4)] hover:shadow-[0_6px_20px_rgba(37,99,235,0.55)] hover:-translate-y-0.5 shrink-0 overflow-hidden"
          >
            <span className="absolute inset-0 bg-gradient-to-r from-[#2563EB] via-[#3B82F6] to-[#2563EB] opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            <span className="relative">Book a Free Consultation</span>
            <ArrowUpRight size={15} className="relative transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
};
